import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Alert, Button, Tabs} from "@mantine/core";
import { CurrencyDollar as IconCash, ArrowClockwise as IconRefresh, CheckSquare as IconSquareCheck } from "@phosphor-icons/react";
import {DataTable} from "mantine-datatable";
import {useCallback, useContext, useMemo, useState} from "react";
import LoaderScreen from "../../components/Loader/LoaderScreen";
import Page from "../../components/Page/Page";
import MessageContext from "../../context/MessageContext";
import api, {useUser} from "../../utils/api";
import {bodyNumber} from "../../utils/formatters";
import {bodyLicenseId, bodyLicenseType, bodyStatus, bodyTimestamp, bodyType, statusColor, transactionPayDateBody} from "./shared";
import {usePaySubscribe} from "./usePaySubscribe";
import useDataTableSessionState from "../../hooks/useDataTableSessionState.ts";


const bodyVoucherStatus = (row) => {
    let status = row.status;
    if(row.status === 'available' && row.targetUser) {
        status =  'redeemed'
    }
    const color = status in statusColor ? statusColor[status] : 'text-gray-500';

    return <span className={`capitalize ${color}`}>{status}</span>;
}

const TAB_PAYMENTS = "payments";
const TAB_SUBSCRIPTIONS = "subscriptions";
const TAB_GIFTS = "gifts";
const TAB_VOUCHERS = "vouchers";

export default function ManageSubscription() {
    const {success, error} = useContext(MessageContext);
    const {user} = useUser();
    const queryClient = useQueryClient();

    const {verifying, verifyTransaction, openPayment} = usePaySubscribe({
        onSuccess: (message) => {
            success(message);
            queryClient.invalidateQueries(['transactions']);
            queryClient.invalidateQueries(['user']);
        },
        onError: (title, message) => {
            error(title, message);
        }
    })
    const {data, isPending} = useQuery({
        queryFn: async () => {
            const req = await api.get(`/subscribe/transactions`)
            if(req.data.transactions.some(t => !t.payDate)) {
                setActiveTab(TAB_PAYMENTS)
            }

            return req.data;
        },
        queryKey: ['transactions']
    })
    const [activeTab, setActiveTab] = useState(TAB_SUBSCRIPTIONS);

    const paymentsTableState = useDataTableSessionState({
        storageKey: 'subscriptions:manage:payments',
        initialSortStatus: {columnAccessor: 'created', direction: 'desc'},
    });
    const subscriptionsTableState = useDataTableSessionState({
        storageKey: 'subscriptions:manage:subscriptions',
        initialSortStatus: {columnAccessor: 'created', direction: 'desc'},
    });
    const giftsTableState = useDataTableSessionState({
        storageKey: 'subscriptions:manage:gifts',
        initialSortStatus: {columnAccessor: 'created', direction: 'desc'},
    });
    const vouchersTableState = useDataTableSessionState({
        storageKey: 'subscriptions:manage:vouchers',
        initialSortStatus: {columnAccessor: 'created', direction: 'desc'},
    });

    const activateLicense = useCallback((id) => {
        api.get(`/subscribe/activate/${id}`).then(() => {
            success('License activated', 'Your subscription is now active!');
            queryClient.invalidateQueries(['transactions']);
            queryClient.invalidateQueries(['user']);
        }).catch((err) => {
            error('Activation failed', err.response?.data?.error || 'An error occurred during activation');
        });
    }, [success, error, queryClient]);

    const bodyTransactionActions = useCallback((row) => {
        if(!row.payDate) {
            return (
                <div className="flex gap-2">
                    <Button leftSection={<IconCash size={16}/>} size="compact-sm" variant="default" onClick={() => openPayment(row.id, row.link)}>Pay</Button>
                    <Button leftSection={<IconRefresh size={16}/>} size="compact-sm" variant="default" onClick={() => verifyTransaction(row.id)}>Verify</Button>
                </div>
            )
        }
    }, [openPayment, verifyTransaction]);

    const bodySubscriptionActions = useCallback((row) => {
        if(row.status === 'available' && user.subLevel === 0) {
            return (
                <div className="flex gap-2">
                    <Button leftSection={<IconSquareCheck size={16}/>} size="compact-sm" variant="default" onClick={() => activateLicense(row.id)}>Activate</Button>
                </div>
            )
        }
    }, [user.subLevel, activateLicense]);

    // Sorted data for each tab
    const sortedTransactions = useMemo(() => {
        if (!data?.transactions) return [];
        const sorted = [...data.transactions].sort((a, b) => {
            const aVal = a[paymentsTableState.sortStatus.columnAccessor];
            const bVal = b[paymentsTableState.sortStatus.columnAccessor];
            if (typeof aVal === 'string') return aVal.localeCompare(bVal);
            return (aVal ?? 0) - (bVal ?? 0);
        });
        return paymentsTableState.sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
    }, [data?.transactions, paymentsTableState.sortStatus]);

    const sortedLicenses = useMemo(() => {
        if (!data?.licenses) return [];
        const sorted = [...data.licenses].sort((a, b) => {
            const aVal = a[subscriptionsTableState.sortStatus.columnAccessor];
            const bVal = b[subscriptionsTableState.sortStatus.columnAccessor];
            if (typeof aVal === 'string') return aVal.localeCompare(bVal);
            return (aVal ?? 0) - (bVal ?? 0);
        });
        return subscriptionsTableState.sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
    }, [data?.licenses, subscriptionsTableState.sortStatus]);

    const sortedGifts = useMemo(() => {
        if (!data?.gifts) return [];
        const sorted = [...data.gifts].sort((a, b) => {
            const aVal = a[giftsTableState.sortStatus.columnAccessor];
            const bVal = b[giftsTableState.sortStatus.columnAccessor];
            if (typeof aVal === 'string') return aVal.localeCompare(bVal);
            return (aVal ?? 0) - (bVal ?? 0);
        });
        return giftsTableState.sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
    }, [data?.gifts, giftsTableState.sortStatus]);

    const sortedVouchers = useMemo(() => {
        if (!data?.vouchers) return [];
        const sorted = [...data.vouchers].sort((a, b) => {
            const aVal = a[vouchersTableState.sortStatus.columnAccessor];
            const bVal = b[vouchersTableState.sortStatus.columnAccessor];
            if (typeof aVal === 'string') return aVal.localeCompare(bVal);
            return (aVal ?? 0) - (bVal ?? 0);
        });
        return vouchersTableState.sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
    }, [data?.vouchers, vouchersTableState.sortStatus]);

    if(isPending) {
        return null;
    }


    return (
        <Page>
            <title>Manage Subscription</title>
            {verifying && <LoaderScreen message="Verifying payment" />}
            <Tabs value={activeTab} onChange={setActiveTab}>
                <Tabs.List>
                    <Tabs.Tab value={TAB_PAYMENTS}>Payments</Tabs.Tab>
                    <Tabs.Tab value={TAB_SUBSCRIPTIONS}>Subscriptions</Tabs.Tab>
                    <Tabs.Tab value={TAB_GIFTS}>Sent Gifts</Tabs.Tab>
                    <Tabs.Tab value={TAB_VOUCHERS}>Purchased Vouchers</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value={TAB_PAYMENTS}>
                    <DataTable
                        records={sortedTransactions}
                        sortStatus={paymentsTableState.sortStatus}
                        onSortStatusChange={paymentsTableState.setSortStatus}
                        columns={[
                            {accessor: 'id', title: 'ID', render: (row) => bodyLicenseId(row, {field: 'id'})},
                            {accessor: 'created', title: 'Created', render: (row) => bodyTimestamp(row, {field: 'created'})},
                            {accessor: 'price', title: 'Total', render: (row) => bodyNumber(row, {field: 'price'})},
                            {accessor: 'licenses', title: 'License Type', render: (row) => bodyLicenseType(row)},
                            {accessor: 'payDate', title: 'Paid', render: (row) => transactionPayDateBody(row)},
                            {accessor: 'actions', title: 'Actions', render: (row) => bodyTransactionActions(row)},
                        ]}
                    />
                </Tabs.Panel>
                <Tabs.Panel value={TAB_SUBSCRIPTIONS}>
                    <DataTable
                        records={sortedLicenses}
                        sortStatus={subscriptionsTableState.sortStatus}
                        onSortStatusChange={subscriptionsTableState.setSortStatus}
                        columns={[
                            {accessor: 'id', title: 'ID', render: (row) => bodyLicenseId(row, {field: 'id'})},
                            {accessor: 'created', title: 'Created', render: (row) => bodyTimestamp(row, {field: 'created'})},
                            {accessor: 'payDate', title: 'Pay Date', render: (row) => bodyTimestamp(row, {field: 'payDate'})},
                            {accessor: 'type', title: 'Type', render: (row) => bodyType(row)},
                            {accessor: 'expireDate', title: 'Valid until', render: (row) => bodyTimestamp(row, {field: 'expireDate'})},
                            {accessor: 'status', title: 'Status', render: (row) => bodyStatus(row)},
                            {accessor: 'actions', title: 'Actions', render: (row) => bodySubscriptionActions(row)},
                        ]}
                    />
                </Tabs.Panel>
                <Tabs.Panel value={TAB_GIFTS}>
                    <DataTable
                        records={sortedGifts}
                        sortStatus={giftsTableState.sortStatus}
                        onSortStatusChange={giftsTableState.setSortStatus}
                        noRecordsText="No gifts sent"
                        columns={[
                            {accessor: 'id', title: 'ID', render: (row) => bodyLicenseId(row, {field: 'id'})},
                            {accessor: 'created', title: 'Created', render: (row) => bodyTimestamp(row, {field: 'created'})},
                            {accessor: 'payDate', title: 'Pay Date', render: (row) => bodyTimestamp(row, {field: 'payDate'})},
                            {accessor: 'targetUser.name', title: 'For'},
                            {accessor: 'status', title: 'Status', render: (row) => bodyStatus(row)},
                        ]}
                    />
                </Tabs.Panel>
                <Tabs.Panel value={TAB_VOUCHERS}>
                    <Alert className="mb-2" color="blue">To share the voucher give the ID to the intended recipient. They can redeem it from the 'Subscribe' page.</Alert>
                    <DataTable
                        records={sortedVouchers}
                        sortStatus={vouchersTableState.sortStatus}
                        onSortStatusChange={vouchersTableState.setSortStatus}
                        noRecordsText="No vouchers purchased"
                        columns={[
                            {accessor: 'id', title: 'ID / Voucher Code', render: (row) => bodyLicenseId(row, {field: 'id'})},
                            {accessor: 'created', title: 'Created', render: (row) => bodyTimestamp(row, {field: 'created'})},
                            {accessor: 'payDate', title: 'Pay Date', render: (row) => bodyTimestamp(row, {field: 'payDate'})},
                            {accessor: 'targetUser.name', title: 'Redeemed by'},
                            {accessor: 'status', title: 'Status', render: (row) => bodyVoucherStatus(row)},
                        ]}
                    />
                </Tabs.Panel>
            </Tabs>
        </Page>
    )
}
